import { z } from 'zod';

import { localeSchema } from './locales.js';
import {
  DataState,
  csvParam,
  daySchema,
  listQuerySchema,
  relativeRangeSchema,
  searchParam,
  sortDirSchema,
  type DataFreshness,
} from './common.js';
import {
  entityStatusSchema,
  type AdPlatform,
  type EntityStatus,
  type MetricLevel,
} from './enums.js';

/**
 * ANALYTICS — normalize metrik sorgusu, hiyerarşi, raporlar, dışa aktarma.
 *
 * Tasarım §4.1 (dashboard), §5.4 (metrics_daily), §5.3.4.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Dashboard platform API'sine GİTMEZ
 * ---------------------------------------------------------------------------
 * Her okuma kendi veritabanımızdan (§5.3.4). Connector çağıran bir analytics
 * kodu sözleşme ihlalidir ve testte kontrol edilir. Gerekçe: platform API'si
 * yavaş, kotalı ve dışarıdan gelen bir bağımlılık; kullanıcının her gün
 * açtığı ekran ona bağlı olamaz.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Türetilmiş metrikler toplamlarda YENİDEN HESAPLANIR
 * ---------------------------------------------------------------------------
 * CTR/CPC/CPM/ROAS için satır ortalaması ALINMAZ:
 *     ctr  = sum(clicks) / sum(impressions)
 *     cpc  = sum(spend)  / sum(clicks)
 *     cpm  = sum(spend)  / sum(impressions) * 1000
 *     roas = sum(revenue)/ sum(spend)
 * Sıfıra bölme `null` verir, `0` değil — "veri yok" ile "sıfır" farklı şeyler
 * ve grafikte 0 çizmek yalan.
 *
 * `MetricDaily.ctr/cpc/cpm/roas` kolonları YALNIZCA satır düzeyinde
 * sıralama/filtre içindir. Toplamda kullanılırsa 100 gösterimlik bir gün ile
 * 1.000.000 gösterimlik bir gün eşit ağırlık alır ve sayı yanlış çıkar.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Para birimi karışımı
 * ---------------------------------------------------------------------------
 * `MetricDaily`'de para birimi kolonu YOK; `AdAccount.currency` hesap başına
 * değişebiliyor ve `Workspace.currency` üçüncü bir değer. Farklı para birimli
 * iki hesabın harcamasını toplamak MATEMATİKSEL OLARAK YANLIŞ bir sayı üretir.
 *
 * Sözleşme kararı: toplamlar HER ZAMAN para birimi bilgisiyle döner.
 *   - Kapsamdaki tüm hesaplar aynı para birimindeyse: `currency` dolu,
 *     `mixedCurrency: false`, tek bir toplam basılabilir.
 *   - Karışıksa: `currency: null`, `mixedCurrency: true` ve `byCurrency[]`
 *     dolu. **Arayüz bu durumda tek bir toplam BASMAZ**, kırılımı gösterir.
 *
 * Kalıcı çözüm bir şema değişikliği ister (`MetricDaily.spendUsd` + kur);
 * gerekçesiyle `semaDegisikligi`'nde bildirildi.
 *
 * ---------------------------------------------------------------------------
 * Gün sınırı ve zaman dilimi
 * ---------------------------------------------------------------------------
 * `MetricDaily.date` platform HESABININ zaman dilimindeki güne göre geliyor.
 * Workspace kendi zaman dilimini seçiyor. Otorite bölünmüş ve bu bilinçli:
 *   - VERİ için otorite hesap zaman dilimi (platformdan öyle geliyor, biz
 *     yeniden dilimleyemeyiz — gün içi kırılım yok).
 *   - SUNUM ve ZAMANLAMA için otorite workspace zaman dilimi ("bugün",
 *     "son 7 gün" ve kural tetikleme saatleri buna göre hesaplanır).
 * Arayüz karışık hesaplı workspace'te bunu bir dipnot olarak yazar.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Metrik sözlüğü — TEK KAYNAK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kolon seçici (KM8), rapor oluşturucu (RP2) ve ajan araçları AYNI sözlüğü
 * okur. Üç yerde ayrı liste tutulursa ekranlar ayrışır ve kullanıcı raporda
 * seçebildiği bir metriği tabloda bulamaz.
 */
export const METRIC_KEYS = [
  'spend',
  'impressions',
  'clicks',
  'conversions',
  'revenue',
  'ctr',
  'cpc',
  'cpm',
  'cpa',
  'roas',
  /// Faz 2 — `metrics_daily.reach` / `.frequency` kolonları Faz 1'de açıldı.
  /// İkisi de GÜN İÇİNDE gerçek, günler arasında toplanamaz (aşağıya bak).
  'reach',
  'frequency',
  /// Faz 7 — e-ticaret hunisi. Gerekçe ve `null` semantiği `FUNNEL_METRIC_KEYS`
  /// başlığında; şema kararı (ayrı tablo DEĞİL, kolon) `metrics_daily`de.
  'addToCart',
  'addToCartValue',
  'initiateCheckout',
  'initiateCheckoutValue',
  'purchases',
  'purchaseValue',
  /// Genişleme Adım 10 — Google arama ağı gösterim payı. Beşi de ORAN ve
  /// hiçbiri toplanmaz; aralık değeri `impressionShareOverRange` ile yeniden
  /// türetiliyor. Gerekçe `IMPRESSION_SHARE_METRIC_KEYS` başlığında.
  'searchImpressionShare',
  'searchTopImpressionShare',
  'searchAbsoluteTopImpressionShare',
  'searchBudgetLostImpressionShare',
  'searchRankLostImpressionShare',
] as const;
export type MetricKey = (typeof METRIC_KEYS)[number];
export const metricKeySchema = z.enum(METRIC_KEYS);

export interface MetricDefinition {
  key: MetricKey;
  /// 'currency' → para birimi işaretiyle, 'count' → tam sayı,
  /// 'ratio' → yüzde, 'rate' → ondalıklı oran (ROAS gibi "3,4x").
  unit: 'currency' | 'count' | 'ratio' | 'rate';
  /// Toplamlarda nasıl üretilir. `derived` olanlar ASLA ortalanmaz.
  aggregation: 'sum' | 'derived';
  /// Büyük olması iyi mi. Değişim yüzdesinin rengini bu belirler —
  /// CPC'de %20 artış KIRMIZI, ROAS'ta yeşil.
  betterWhen: 'higher' | 'lower';
  /// Ondalık basamak sayısı (gösterim).
  precision: number;
  /// Hangi seviyelerde anlamlı.
  levels: MetricLevel[];
  /**
   * 🔴 HANGİ ARALIKTA GERÇEK BİR DEĞER ÜRETİLEBİLİR (Faz 2 sözleşme kararı).
   *
   *   'any'       → her aralıkta hesaplanır. Ham toplamlar toplanabilir,
   *                 türetilmişler `sum/sum` ile yeniden hesaplanır.
   *   'singleDay' → YALNIZ tek günlük aralıkta. Daha geniş aralıkta değer
   *                 `null` döner ve arayüz `—` basar.
   *
   * `singleDay`in tek sebebi TEKİLLİK: `reach` tekil kişi sayısı. Pazartesi
   * 1.000 kişiye, salı 1.000 kişiye ulaşan bir reklamın iki günlük erişimi
   * 2.000 DEĞİL — aynı kişiler iki gün de görmüş olabilir. Elimizde yalnız
   * günlük satır var; günler arası tekilleştirmeyi yapabilecek tek yer
   * platformun kendisi ve o veriyi gün kırılımıyla birlikte VERMİYOR.
   *
   * Toplayıp basmak, kullanıcının erişimini 5–20 kat şişirir ve `frequency`i
   * (gösterim/erişim) aynı oranda KÜÇÜLTÜR — yani kreatif yorgunluğunu tam
   * ölçmesi gereken sayı, tam da yorgunluk arttıkça yalan söyler.
   *
   * Bunun bedeli: 30 günlük panelde erişim kutusu boş. Kabul edildi;
   * bilinmeyen bir sayının yerine uydurulmuş bir sayı koymak daha pahalı.
   * Dönem erişimi ancak platformdan dönem sorgusuyla gelebilir —
   * `metrics_daily`'ye sığmaz, ayrı bir tablo ister (Faz 6 önerisi).
   */
  aggregatableOver: 'any' | 'singleDay';
}

/// Tüm platformlarda dört seviyede de anlamlı olan metriklerin ortak listesi.
/// `KEYWORD` burada YOK: kelime seviyesi yalnız arama ağında var ve okuma
/// yolu Faz 5'te açılıyor (`analytics.sql.ts` `ENTITY_TABLE` bugün `null`).
const ALL_LEVELS: MetricLevel[] = ['ACCOUNT', 'CAMPAIGN', 'ADSET', 'AD'];

export const METRIC_DEFINITIONS: Record<MetricKey, MetricDefinition> = {
  spend: {
    key: 'spend',
    unit: 'currency',
    aggregation: 'sum',
    betterWhen: 'lower',
    precision: 2,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },
  impressions: {
    key: 'impressions',
    unit: 'count',
    aggregation: 'sum',
    betterWhen: 'higher',
    precision: 0,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },
  clicks: {
    key: 'clicks',
    unit: 'count',
    aggregation: 'sum',
    betterWhen: 'higher',
    precision: 0,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },
  conversions: {
    key: 'conversions',
    unit: 'count',
    aggregation: 'sum',
    betterWhen: 'higher',
    precision: 0,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },
  revenue: {
    key: 'revenue',
    unit: 'currency',
    aggregation: 'sum',
    betterWhen: 'higher',
    precision: 2,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },
  ctr: {
    key: 'ctr',
    unit: 'ratio',
    aggregation: 'derived',
    betterWhen: 'higher',
    precision: 2,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },
  cpc: {
    key: 'cpc',
    unit: 'currency',
    aggregation: 'derived',
    betterWhen: 'lower',
    precision: 2,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },
  cpm: {
    key: 'cpm',
    unit: 'currency',
    aggregation: 'derived',
    betterWhen: 'lower',
    precision: 2,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },
  cpa: {
    key: 'cpa',
    unit: 'currency',
    aggregation: 'derived',
    betterWhen: 'lower',
    precision: 2,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },
  roas: {
    key: 'roas',
    unit: 'rate',
    aggregation: 'derived',
    betterWhen: 'higher',
    precision: 2,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },

  /**
   * Tekil kişi sayısı. `aggregation: 'derived'` çünkü TOPLANAMAZ — ama
   * `ctr`den farklı olarak yeniden hesaplanabileceği bir formül de yok;
   * `aggregatableOver: 'singleDay'` kısıtı bunu söylüyor.
   *
   * `betterWhen: 'higher'` bilinçli: erişimin düşmesi tek başına kötü değil
   * (daralan hedefleme kasıtlı olabilir) ama panelde bir yön göstermek
   * zorundayız ve kullanıcının beklentisi "daha çok kişi = daha iyi".
   */
  reach: {
    key: 'reach',
    unit: 'count',
    aggregation: 'derived',
    betterWhen: 'higher',
    precision: 0,
    levels: ALL_LEVELS,
    aggregatableOver: 'singleDay',
  },

  /**
   * gösterim / erişim — KREATİF YORGUNLUĞUNUN ANA GÖSTERGESİ.
   *
   * `betterWhen: 'lower'`: aynı kişiye ısrarla gösterim, tıklama oranını
   * düşürüp CPM'i yükseltiyor. Eşiği ürün DEĞİL kullanıcı seçiyor (kural
   * editöründe); burada yalnız değişimin rengini belirliyor.
   *
   * `reach` gibi `singleDay`: paydası erişim olduğu için onun kısıtını
   * aynen devralıyor. Dönem frekansı (gerçek "bu kişi 30 günde kaç kez
   * gördü") ancak platformdan dönem sorgusuyla gelir.
   */
  frequency: {
    key: 'frequency',
    unit: 'rate',
    aggregation: 'derived',
    betterWhen: 'lower',
    precision: 2,
    levels: ALL_LEVELS,
    aggregatableOver: 'singleDay',
  },

  // ── Huni — Faz 7 ─────────────────────────────────────────────────────────
  //
  // Altısı da ham toplam: gün gün toplanabilirler, `reach` gibi bir tekillik
  // sorunları yok (aynı kişi iki gün de sepete ekleyebilir ve bu İKİ olaydır).
  addToCart: {
    key: 'addToCart',
    unit: 'count',
    aggregation: 'sum',
    betterWhen: 'higher',
    precision: 0,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },
  addToCartValue: {
    key: 'addToCartValue',
    unit: 'currency',
    aggregation: 'sum',
    betterWhen: 'higher',
    precision: 2,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },
  initiateCheckout: {
    key: 'initiateCheckout',
    unit: 'count',
    aggregation: 'sum',
    betterWhen: 'higher',
    precision: 0,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },
  initiateCheckoutValue: {
    key: 'initiateCheckoutValue',
    unit: 'currency',
    aggregation: 'sum',
    betterWhen: 'higher',
    precision: 2,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },
  purchases: {
    key: 'purchases',
    unit: 'count',
    aggregation: 'sum',
    betterWhen: 'higher',
    precision: 0,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },
  purchaseValue: {
    key: 'purchaseValue',
    unit: 'currency',
    aggregation: 'sum',
    betterWhen: 'higher',
    precision: 2,
    levels: ALL_LEVELS,
    aggregatableOver: 'any',
  },

  // ── Gösterim payı — Genişleme Adım 10 ────────────────────────────────────
  //
  // Beşi de `aggregation: 'derived'`: oran oldukları için toplanmazlar. Ama
  // `reach`ten farklı olarak `aggregatableOver: 'any'` — çünkü aralık değeri
  // GERÇEKTEN hesaplanabiliyor (bkz. `IMPRESSION_SHARE_METRIC_KEYS`). `reach`in
  // `singleDay` olmasının sebebi bir formül eksikliği değil, TEKİLLİK: elimizde
  // günler arası kişi tekilleştirmesini yapacak veri yok. Burada öyle bir engel
  // yok, uygun gösterim sayısı iki bilinen sayıdan çıkıyor.
  //
  // `levels` DÖRT DEĞİL İKİ: Google bu metrikleri `campaign` ve `ad_group`
  // kaynaklarında veriyor; `customer` (ACCOUNT) ve `ad_group_ad` (AD)
  // kaynaklarında alan seçilemiyor. `ALL_LEVELS` yazmak, hesap seviyesinde
  // kolon seçicide çıkıp her zaman `—` basan bir metrik üretirdi.
  searchImpressionShare: {
    key: 'searchImpressionShare',
    unit: 'ratio',
    aggregation: 'derived',
    betterWhen: 'higher',
    precision: 2,
    levels: ['CAMPAIGN', 'ADSET'],
    aggregatableOver: 'any',
  },
  searchTopImpressionShare: {
    key: 'searchTopImpressionShare',
    unit: 'ratio',
    aggregation: 'derived',
    betterWhen: 'higher',
    precision: 2,
    levels: ['CAMPAIGN', 'ADSET'],
    aggregatableOver: 'any',
  },
  searchAbsoluteTopImpressionShare: {
    key: 'searchAbsoluteTopImpressionShare',
    unit: 'ratio',
    aggregation: 'derived',
    betterWhen: 'higher',
    precision: 2,
    levels: ['CAMPAIGN', 'ADSET'],
    aggregatableOver: 'any',
  },
  /// 🔴 `betterWhen: 'lower'` — kaybedilen pay AZALDIKÇA iyi. Diğer üçüyle
  /// aynı yöne yazmak, bütçe kaybının artışını panelde YEŞİL gösterirdi.
  searchBudgetLostImpressionShare: {
    key: 'searchBudgetLostImpressionShare',
    unit: 'ratio',
    aggregation: 'derived',
    betterWhen: 'lower',
    precision: 2,
    levels: ['CAMPAIGN', 'ADSET'],
    aggregatableOver: 'any',
  },
  searchRankLostImpressionShare: {
    key: 'searchRankLostImpressionShare',
    unit: 'ratio',
    aggregation: 'derived',
    betterWhen: 'lower',
    precision: 2,
    levels: ['CAMPAIGN', 'ADSET'],
    aggregatableOver: 'any',
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// HUNİ (funnel) — Faz 7
// ═══════════════════════════════════════════════════════════════════════════

/**
 * E-ticaret hunisinin üç adımı, her biri SAYI + DEĞER.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `conversions` bunların TOPLAMI DEĞİL — ve bunlar onun kırılımı da değil
 * ═══════════════════════════════════════════════════════════════════════════
 * `conversions` reklamverenin platformda "dönüşüm" olarak İŞARETLEDİĞİ
 * aksiyonu sayıyor (`CONVERSION_ACTION_PRIORITY` listesinin ilk eşleşeni);
 * lead-gen bir hesapta o aksiyon `lead`, e-ticarette genelde `purchase`.
 * Yani e-ticaret hesabında `purchases` ile `conversions` çoğu zaman AYNI
 * sayıdır ama bu bir garanti değil, bir tesadüf: reklamveren dönüşüm olarak
 * `complete_registration`ı seçmiş olabilir.
 *
 * Bu yüzden ikisi ayrı kolon ve `purchases`, `conversions`ın yerine
 * GEÇMİYOR. Geçseydi lead-gen bir hesapta huni ekranı satın alma diye
 * lead sayısını gösterirdi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `null` = BİLİNMİYOR, `0` = OLMADI
 * ═══════════════════════════════════════════════════════════════════════════
 * Bugün bu üçünü yalnız Meta okuyor (Google için GAQL tarafı yazılmadı,
 * TikTok adapter'ı hiç yok). Google satırlarında kolonlar `NULL` kalıyor ve
 * `SUM` NULL'ları ATLIYOR — yani Meta + Google karışık bir kapsamda huni
 * sayıları YALNIZ Meta'yı anlatıyor, harcama ise ikisini birden.
 *
 * Bu sessiz eksik beyan tam olarak Faz 2'nin kapatmak istediği hata sınıfı.
 * Bu yüzden `AnalyticsSummary.funnel` hangi platformların gerçekten veri
 * verdiğini geri söylüyor (`FunnelCoverage`) ve arayüz karışık kapsamda
 * dipnot basmak zorunda.
 *
 * `0` ile `null` ayrımı bir titizlik değil: pikseli bozulmuş bir hesapta
 * "bugün 0 satın alma" bir ALARM, "veri yok" bir eksiklik. İkisini
 * birleştirmek `lead-flow-stopped-alert` sınıfı kuralları ya sürekli
 * tetikler ya hiç tetiklemez.
 */
export const FUNNEL_METRIC_KEYS = [
  'addToCart',
  'addToCartValue',
  'initiateCheckout',
  'initiateCheckoutValue',
  'purchases',
  'purchaseValue',
] as const satisfies readonly MetricKey[];
export type FunnelMetricKey = (typeof FUNNEL_METRIC_KEYS)[number];

/**
 * Sorgunun kapsamında huni verisini HANGİ platform gerçekten verdi.
 *
 * `AttributionCoverage` ile aynı fikir ve aynı gerekçe: `SUM` sonrası
 * satırın hangi platformdan geldiği kayboluyor, o yüzden ayrıca soruluyor.
 *
 * ⚠️ `missingPlatforms` İKİ AYRI DURUMU birleştiriyor ve birleştirmesi
 * doğru: (a) adapter huni okumuyor (bugün Google, TikTok), (b) adapter
 * okuyor ama reklamverenin pikseli o olayları hiç göndermemiş. Kullanıcı
 * için sonuç aynı — o platformun sepeti/satın alması bu sayılarda YOK — ve
 * ayrımı yapabilecek bilgi elimizde yok: veri gelmemesiyle veri olmaması
 * bizim tarafımızdan ayırt edilemiyor.
 */
export interface FunnelCoverage {
  reportedPlatforms: AdPlatform[];
  missingPlatforms: AdPlatform[];
}

// ═══════════════════════════════════════════════════════════════════════════
// GÖSTERİM PAYI (impression share) — Genişleme Adım 10
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Arama ağı gösterim payı — reklamın ne kadarını KAÇIRDIĞININ tek ölçüsü.
 *
 * Diğer bütün metrikler olan biteni anlatıyor: harcama, tıklama, dönüşüm.
 * Gösterim payı OLMAYANI anlatıyor — "uygun 100 gösterimin 38'inde reklamın
 * hiç çıkmadı, 18'i bütçe yetmediği için". Ürünün "bütçeyi artır" önerisinin
 * tahminden ibaret olmaktan çıkıp kanıta dayanmasının tek yolu bu.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 TOPLANMAZ — ama `reach`in aksine TÜRETİLEBİLİR
 * ═══════════════════════════════════════════════════════════════════════════
 * Pay = gösterim / UYGUN gösterim. İki günün payını toplamak da ortalamak da
 * yanlış: gösterimle ağırlıklandırmak bile yanlış, çünkü ağırlık paydada.
 *
 * Ama uygun gösterim iki bilinen sayıdan çıkıyor:
 *
 *     uygun_gün  = gösterim_gün / pay_gün
 *     pay_aralık = Σ gösterim_gün / Σ uygun_gün
 *
 * Bu bir yaklaşıklık DEĞİL, cebir. `reach` böyle değildi: orada eksik olan
 * bir formül değil, günler arası kişi tekilleştirmesiydi ve o bilgi hiçbir
 * yerde yok. Bu yüzden erişim `singleDay`, gösterim payı `any`.
 *
 * Kaybedilen paylar aynı paydayı kullanıyor (Google beşini de aynı "uygun
 * gösterim" tabanına göre veriyor), yani onlar da uygun gösterimle
 * ağırlıklanıp aynı paydaya bölünüyor.
 *
 * Uygulama: `impressionShareOverRange` (apps/api/src/analytics/metric-math.ts) —
 * türetilmiş metriklerin TEK hesap yeri orası, ikinci bir formül yazılmadı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ GOOGLE UÇLARI KIRPIYOR — sayı olarak
 * ═══════════════════════════════════════════════════════════════════════════
 * Gizlilik gereği %10'un altındaki pay `0,0999` olarak dönüyor. Yani çok
 * düşük paylarda uygun gösterim OLDUĞUNDAN AZ hesaplanır ve aralık payı bir
 * miktar YÜKSEK çıkar. Kırpma sayısal — alanların proto tipi `DOUBLE`
 * (kurulu `google-ads-api@24.1.0` alan tablosundan doğrulandı), yani eski
 * projedeki `"< 0.1"` metin ayrıştırması bu yolda gereksiz.
 *
 * Sapmanın yönü biliniyor ve tek yönlü: gerçek pay hesaplanandan DÜŞÜK
 * olabilir, yüksek olamaz. "Payın %10'un altında" mesajı yine doğru kalıyor
 * ve aksiyon o mesajdan çıkıyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `null` = BİLİNMİYOR, `0` = HİÇ ÇIKMADI
 * ═══════════════════════════════════════════════════════════════════════════
 * Meta'da bu kavramın karşılığı YOK (platform açık artırma büyüklüğünü hiç
 * yayımlamıyor); LinkedIn ve TikTok'ta da yok. Google'ın ACCOUNT ve AD
 * seviyelerinde alan sorgulanamıyor. Üçünde de kolon `NULL` kalıyor ve
 * `SUM` NULL'ları atlıyor — yani karışık kapsamda pay YALNIZ Google arama
 * kampanyalarını anlatırken harcama hepsini anlatıyor. Huni ile birebir aynı
 * sessiz eksik beyan riski, birebir aynı çözüm: `ImpressionShareCoverage`.
 */
export const IMPRESSION_SHARE_METRIC_KEYS = [
  'searchImpressionShare',
  'searchTopImpressionShare',
  'searchAbsoluteTopImpressionShare',
  'searchBudgetLostImpressionShare',
  'searchRankLostImpressionShare',
] as const satisfies readonly MetricKey[];
export type ImpressionShareMetricKey = (typeof IMPRESSION_SHARE_METRIC_KEYS)[number];

/**
 * Kapsamda gösterim payını HANGİ platform bildirdi.
 *
 * `FunnelCoverage` ile aynı fikir, aynı gerekçe ve aynı şekil — ama iki
 * anlamı ayırıyor olması bakımından ondan DAHA keskin: huninin
 * `missingPlatforms`ı "adapter okumuyor" ile "piksel göndermiyor"u
 * birleştiriyordu (ayrım bizde yok). Burada ayrım NET: Meta, LinkedIn ve
 * TikTok'ta gösterim payı diye bir şey YOK ve hiç olmayacak — bu bir eksik
 * değil, `canManageKeywords`teki gibi bir KAVRAM yokluğu. Arayüz bunu
 * "yakında" diye göstermemeli.
 */
export interface ImpressionShareCoverage {
  reportedPlatforms: AdPlatform[];
  /// Kapsamda harcaması olan ama gösterim payı bildirmeyen platformlar.
  /// Bugün pratikte Google dışındaki her şey.
  missingPlatforms: AdPlatform[];
}

/**
 * Gösterim payı türetmesinin HAM GİRDİLERİ — veritabanından `SUM` ile gelir.
 *
 * Alanların hiçbiri kullanıcıya gösterilmiyor; tek işleri
 * `impressionShareOverRange`in paydasını ve paylarını taşımak. `RawSums` gibi
 * "hesaplanmış" görünmemesi için ayrı bir tip: bu nesneye sahip bir kod asla
 * doğrudan bir oran basamaz.
 *
 * Tümü `null` olabilir — kapsamda gösterim payı bildiren hiçbir satır yoksa
 * `SUM` NULL döner.
 */
export interface ImpressionShareSums {
  /// Payı BİLİNEN (ve sıfırdan büyük) satırların gösterim toplamı.
  /// ⚠️ `RawSums.impressions` ile aynı şey DEĞİL: o, kapsamdaki BÜTÜN
  /// satırları sayıyor (Meta dâhil). Aynı paydayla kullanmak, Meta
  /// gösterimlerini Google'ın açık artırmasına eklemek olurdu.
  isImpressions: number | null;
  /// Uygun gösterim toplamı: Σ (gösterim / pay).
  isEligible: number | null;
  /// Σ (üst_sıra_payı × uygun_gösterim) — aşağıdaki üçü de aynı biçimde.
  isTopWeighted: number | null;
  isAbsoluteTopWeighted: number | null;
  isBudgetLostWeighted: number | null;
  isRankLostWeighted: number | null;
}

/// Hiçbir gösterim payı kolonu SEÇİLMEMİŞ sorgular için. `EMPTY_FUNNEL_SUMS`
/// ile aynı gerekçe: sıfırla doldurmak, sorgulanmamış bir metriği
/// "sorgulandı, sonuç sıfır" gibi gösterirdi.
export const EMPTY_IMPRESSION_SHARE_SUMS: ImpressionShareSums = {
  isImpressions: null,
  isEligible: null,
  isTopWeighted: null,
  isAbsoluteTopWeighted: null,
  isBudgetLostWeighted: null,
  isRankLostWeighted: null,
};

/**
 * ARALIK GÖSTERİM PAYI — türetmenin TEK yeri.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden `metric-math.ts`te değil de burada
 * ═══════════════════════════════════════════════════════════════════════════
 * API'nin `deriveTotals`ı (analytics) ile worker'ın `deriveTotals`ı
 * (`automation-read.service.ts`) AYRI fonksiyonlar ve ayrı derleme
 * birimlerinde. Kural motorunun okuduğu pay ile kullanıcının ekranda gördüğü
 * pay farklı olursa "bu kural neden tetiklendi" sorusu cevapsız kalır —
 * `REACH_SUM` yorumunun söylediği tehlikenin aynısı. Ortak pakete koymak o
 * sapmayı imkânsız kılıyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Formül
 * ═══════════════════════════════════════════════════════════════════════════
 *     pay        = Σ gösterim / Σ uygun
 *     üst_sıra   = Σ (üst_sıra_gün × uygun_gün) / Σ uygun
 *
 * `Σ uygun` sıfır ya da `null` ise hepsi `null`: paydası olmayan bir oran
 * hesaplanamaz ve `0` yazmak "hiç çıkmadı" demek olurdu — oysa bilinmiyor.
 *
 * ⚠️ SONUÇ 1'İ AŞABİLİR mi: hayır, kırpma yapmıyoruz ama aşamaz da —
 * `Σ gösterim ≤ Σ uygun` her günde tanım gereği doğru olduğu için toplamda da
 * doğru. Aşan bir sonuç girdinin bozuk olduğunu söyler ve o bozukluğu
 * gizlemek istemiyoruz.
 *
 * ⚠️ TEK YÖNLÜ İKİ SAPMA, ikisi de bilinçli:
 *   1. Google %10 altı payı `0,0999` olarak veriyor → uygun gösterim
 *      olduğundan AZ hesaplanır → aralık payı bir miktar YÜKSEK çıkar.
 *   2. Payı dolu ama üst sıra/kayıp payı `null` olan bir satır (Google'da
 *      görülmedi ama mümkün) paydada var, payda yok → o metrik bir miktar
 *      DÜŞÜK çıkar.
 * İkisi de sınırlı ve yönü bilinen sapmalar; her metriğe ayrı payda tutmak
 * altı yerine on `SUM` kolonu demekti.
 */
export function impressionShareOverRange(
  sums: ImpressionShareSums,
): Pick<MetricTotals, ImpressionShareMetricKey> {
  const eligible = sums.isEligible;
  if (eligible === null || eligible <= 0) {
    return {
      searchImpressionShare: null,
      searchTopImpressionShare: null,
      searchAbsoluteTopImpressionShare: null,
      searchBudgetLostImpressionShare: null,
      searchRankLostImpressionShare: null,
    };
  }
  const over = (numerator: number | null): number | null =>
    numerator === null ? null : numerator / eligible;

  return {
    searchImpressionShare: over(sums.isImpressions),
    searchTopImpressionShare: over(sums.isTopWeighted),
    searchAbsoluteTopImpressionShare: over(sums.isAbsoluteTopWeighted),
    searchBudgetLostImpressionShare: over(sums.isBudgetLostWeighted),
    searchRankLostImpressionShare: over(sums.isRankLostWeighted),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// ATIF (attribution) — Faz 2 sözleşme kararı, uygulama Faz 6
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// VİDEO İZLENME — Dalga 5 (2 Eki 2026)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Video reklamın izlenme metrikleri — `metrics_daily`nin NULLABLE kolonları
 * (Dalga 5 göçü; senkron şeridi B2 yazar). Kreatif stüdyonun "performans önce"
 * kararının (plan §4) ölçüsü: hangi açılış kaydırmayı durdurdu, hangi reklam
 * sonuna kadar izlendi. Marka hafızasının PERFORMANCE notları ve açılış
 * setinin kazananı bunlardan türer.
 *
 * 🔴 `METRIC_KEYS`te DEĞİL (bilerek, şimdilik): o liste kolon seçicinin, rapor
 * SQL'inin (`analytics.sql.ts` `METRIC_EXPR`), dışa aktarmanın ve 13 dilin
 * etiketlerinin TEK kaynağı; anahtar eklemek o yüzeylerin hepsini aynı anda
 * ister. Bu kolonlar önce senkronla dolsun, ekran yüzeyi ayrı bir turda.
 *
 * Anlamlar (sayı = TAM, oran değil; `null` = BİLİNMİYOR, `0` = OLMADI):
 *   videoPlays      oynatma başlangıcı (Meta `video_play_actions`, TikTok
 *                   `video_play_actions`, Google `video_views` DEĞİL — o
 *                   TrueView "izlendi" sayısı, `videoThruplays`a yakın)
 *   video3sViews    "durdu" eşiği: Meta 3 sn izlenme. 🔴 TikTok'ta eşik 2 sn
 *                   (`video_watched_2s`) — AYNI kolona yazılır, platform
 *                   satırın `platform` kolonundan bilinir; platformlar arası
 *                   karşılaştırmada bu fark söylenir (`VIDEO_HOOK_THRESHOLD_SEC`)
 *   videoThruplays  Meta ThruPlay (15 sn ya da sonuna kadar); TikTok'ta
 *                   karşılığı yok (`null`); Google TrueView izlenmesi buraya
 *   videoP25..P100  videonun %25/%50/%75/%100'üne kadar izlenme sayısı
 *                   (Google çeyrek ORAN verir — adapter gösterimle çarpar)
 *   videoAvgWatchMs oynatma başına ortalama izleme (ms) — TOPLANAMAZ:
 *                   aralık değeri oynatma ağırlıklı ortalama (`videoMetricRates`)
 */
export const VIDEO_METRIC_KEYS = [
  'videoPlays',
  'video3sViews',
  'videoThruplays',
  'videoP25',
  'videoP50',
  'videoP75',
  'videoP100',
  'videoAvgWatchMs',
] as const;
export type VideoMetricKey = (typeof VIDEO_METRIC_KEYS)[number];

/// Platformun "durdu" eşiği (sn) — `video3sViews` kolonunun platforma göre anlamı.
export const VIDEO_HOOK_THRESHOLD_SEC: Readonly<Record<string, number>> = {
  META: 3,
  TIKTOK: 2,
  GOOGLE: 3,
  LINKEDIN: 3,
  X: 3,
};

/// Bir satırın (ya da aralık toplamının) video sayıları.
export type VideoMetrics = Record<VideoMetricKey, number | null>;

export const EMPTY_VIDEO_METRICS: VideoMetrics = {
  videoPlays: null,
  video3sViews: null,
  videoThruplays: null,
  videoP25: null,
  videoP50: null,
  videoP75: null,
  videoP100: null,
  videoAvgWatchMs: null,
};

/**
 * Video oranları — payda 0 ya da bilinmiyorsa `null` (`deriveMetrics` kuralı):
 *   hookRate        "durdu" / gösterim — açılışın kaydırmayı durdurma oranı
 *   holdRate        ThruPlay / "durdu" — durduranın sona kadar kalma oranı
 *   completionRate  %100 / oynatma
 *   avgWatchMs      oynatma ağırlıklı ortalama izleme (aralıkta günlerin
 *                   ortalaması DEĞİL: Σ(ort × oynatma) ÷ Σ oynatma)
 * Aralık toplamı için `rows` günlük satırları alır; sayılar toplanır.
 *
 * 🔴 Açılış oranının paydası YALNIZ video verisi olan satırların gösterimi
 * (`videoImpressions`): video kolonu NULL satırlar (2 Eki yayınından önceki
 * günler, video alanı reddedilen günler, hiç video vermeyen Google) payı
 * eklemeden paydayı büyütüyordu — 25 günlük varyantın oranı ~%20'sine
 * iniyor, A/B kararı ters çıkıyordu (denetim bulgusu, 2 Eki 2026).
 */
export function videoMetricRates(
  rows: readonly (Partial<VideoMetrics> & { impressions: number })[],
): {
  totals: VideoMetrics;
  hookRate: number | null;
  holdRate: number | null;
  completionRate: number | null;
  avgWatchMs: number | null;
  /// Video verisi (`video3sViews`) olan satırların gösterimi — açılış oranının paydası.
  videoImpressions: number;
} {
  const totals: VideoMetrics = { ...EMPTY_VIDEO_METRICS };
  let videoImpressions = 0;
  let weighted = 0;
  let weightedPlays = 0;
  for (const row of rows) {
    if (row.video3sViews !== null && row.video3sViews !== undefined) {
      videoImpressions += row.impressions;
    }
    for (const key of VIDEO_METRIC_KEYS) {
      if (key === 'videoAvgWatchMs') continue;
      const value = row[key];
      if (value === null || value === undefined) continue;
      totals[key] = (totals[key] ?? 0) + value;
    }
    if (row.videoAvgWatchMs != null && row.videoPlays != null && row.videoPlays > 0) {
      weighted += row.videoAvgWatchMs * row.videoPlays;
      weightedPlays += row.videoPlays;
    }
  }
  const ratio = (a: number | null, b: number | null) =>
    a !== null && b !== null && b > 0 ? a / b : null;
  totals.videoAvgWatchMs = weightedPlays > 0 ? Math.round(weighted / weightedPlays) : null;
  return {
    totals,
    hookRate: ratio(totals.video3sViews, videoImpressions > 0 ? videoImpressions : null),
    holdRate: ratio(totals.videoThruplays, totals.video3sViews),
    completionRate: ratio(totals.videoP100, totals.videoPlays),
    avgWatchMs: totals.videoAvgWatchMs,
    videoImpressions,
  };
}

/**
 * 🔴 BUGÜNE KADAR KODDA TEK SATIR ATIF YOKTU — ve bu sessiz bir yalandı.
 *
 * `metrics_daily.conversions` platformdan geldiği gibi yazılıyor; Meta o
 * satırı KENDİ varsayılan penceresiyle (7 günlük tıklama + 1 günlük görüntüleme)
 * üretiyor, Google kendi varsayılanıyla, TikTok kendi varsayılanıyla. Yani
 * "toplam dönüşüm" kutusundaki sayı üç farklı kuralla sayılmış üç sayının
 * toplamı. Kimse fark etmiyor çünkü sayı MAKUL görünüyor.
 *
 * Bunun somut sonucu: aynı satın alma Meta'da görüntüleme atfıyla, Google'da
 * son tıklamayla sayılabiliyor ve platformlar arası ROAS karşılaştırması —
 * bu ürünün ana vaadi — yanlış platformu kazanan ilan ediyor.
 *
 * ---------------------------------------------------------------------------
 * Karar
 * ---------------------------------------------------------------------------
 * 1. Atıf penceresi ve modeli SORGUNUN parçası, kaydın değil. `metrics_daily`
 *    şeması donmuş ve pencere başına ayrı kolon tutmak (7 pencere × 2 kolon)
 *    hem tabloyu şişirir hem her yeni pencerede migration ister.
 * 2. VARSAYILAN `7d_click_1d_view` + `last_click`. Meta'nın varsayılanı bu ve
 *    bugün veritabanındaki geçmiş satırların ÇOĞU bu pencereyle yazıldı;
 *    başka bir varsayılan seçmek, geçmişi bir gecede başka bir şeye
 *    dönüştürmek olurdu.
 * 3. Seçim WORKSPACE düzeyinde yapılır, sorgu başına EZİLEBİLİR. Kullanıcı
 *    "bizim şirkette dönüşüm son tıklamadır" diyebilmeli ve tek bir raporda
 *    bunu değiştirip karşılaştırabilmeli.
 * 4. Yanıt HANGİ ATIFLA üretildiğini geri söyler. Söylemezse iki ekran
 *    farklı sayı gösterdiğinde sebebi bulmak imkânsız.
 *
 * ---------------------------------------------------------------------------
 * ✅ Faz 6 — PENCERE UYGULANIYOR, MODEL UYGULANMIYOR
 * ---------------------------------------------------------------------------
 * `metric_attribution_daily` tablosu (Faz 6, kullanıcı onaylı şema kararı)
 * pencere başına dönüşüm ve geliri saklıyor. Sorgu artık seçilen pencerenin
 * satırını okuyor; o satır yoksa `metrics_daily`nin platform varsayılanına
 * DÜŞÜYOR ve yanıt bunu `unsupportedPlatforms` ile geri söylüyor.
 *
 * 🔴 MODEL (`last_click` / `first_click` / `linear`) HÂLÂ UYGULANMIYOR ve bu
 * kalıcı bir sınır, eksik bir iş değil. Atıf modeli, bir dönüşüme giden
 * TEMAS YOLUNU (hangi reklam önce, hangisi sonra) bilmeyi gerektirir.
 * Hiçbir platform bize temas yolu vermiyor — her biri yalnız KENDİ içinde,
 * kendi modeliyle saydığı sonucu veriyor. Elimizdeki veriyle "ilk tıklama"
 * hesaplamak, olmayan bir bilgiyi uydurmak olurdu.
 *
 * Bu yüzden `AttributionInfo` iki ayrı bayrak taşıyor: `applied` pencereyi,
 * `modelApplied` modeli anlatıyor. Tek bayrak olsaydı, pencere uygulandığında
 * modelin de uygulandığı sanılırdı — ve platformlar arası ROAS
 * karşılaştırması yine sessizce yanlış olurdu, yani Faz 2'nin kapatmak
 * istediği hatanın aynısı.
 */
export const ATTRIBUTION_WINDOWS = [
  '1d_click',
  '7d_click',
  '28d_click',
  '1d_click_1d_view',
  '7d_click_1d_view',
  '28d_click_1d_view',
] as const;
export type AttributionWindow = (typeof ATTRIBUTION_WINDOWS)[number];
export const attributionWindowSchema = z.enum(ATTRIBUTION_WINDOWS);

/**
 * Atıf modeli — dönüşüm kredisi hangi temasa yazılıyor.
 *
 * `data_driven` BİLEREK YOK: platformun kendi makine öğrenmesi modeli, bize
 * yalnız sonucu veriyor ve platformlar arası normalize edilemiyor. Onu
 * listeye koymak, karşılaştırılamayan bir sayıyı karşılaştırılabilir gibi
 * göstermek olurdu (§14 kapsam dışı gerekçesiyle aynı çizgi).
 */
export const ATTRIBUTION_MODELS = ['last_click', 'first_click', 'linear'] as const;
export type AttributionModel = (typeof ATTRIBUTION_MODELS)[number];
export const attributionModelSchema = z.enum(ATTRIBUTION_MODELS);

export const attributionSchema = z.object({
  window: attributionWindowSchema.default('7d_click_1d_view'),
  model: attributionModelSchema.default('last_click'),
});
export type Attribution = z.infer<typeof attributionSchema>;

/// Ürünün varsayılanı. Workspace ayarı yoksa ve sorgu ezmediyse bu geçerli.
export const DEFAULT_ATTRIBUTION: Attribution = {
  window: '7d_click_1d_view',
  model: 'last_click',
};

/**
 * Yanıtın "bu sayılar hangi kuralla sayıldı" cevabı.
 *
 * Faz 2–5 boyunca `applied` sabit `false`ti ve arayüz atıf seçicisini
 * gizliyordu — kullanıcının değiştirdiği ama hiçbir şeyi değiştirmeyen bir
 * kutu, kutuyu hiç koymamaktan kötüdür. Faz 6'da seçici AÇILDI.
 *
 * 🔴 Ama "açıldı" her platformda açıldı demek DEĞİL. Meta pencere başına
 * veri veriyor, Google Ads sorgu zamanında pencere kabul etmiyor (atıf
 * ayarı dönüşüm eyleminin kendisinde). Bu yüzden kapsam KARIŞIK olabilir ve
 * yanıt bunu satır satır söylüyor: aksi hâlde kullanıcı "28 günlük tıklama"
 * seçer, Google tarafı değişmez ve toplam sayı hem yeni hem eski kuralın
 * karışımı olur — Faz 2'nin şikâyet ettiği sessiz yalanın aynısı.
 */
export interface AttributionInfo extends Attribution {
  /// Seçilen PENCERE en az bir platformda gerçekten uygulandı mı.
  /// `appliedPlatforms.length > 0` ile aynı şey; arayüzün seçiciyi
  /// göstermek için tek bir bayrağa bakabilmesi için duruyor.
  applied: boolean;
  /// Pencere başına verisi bulunan ve bu sorguda KULLANILAN platformlar.
  appliedPlatforms: AdPlatform[];
  /// Kapsamda harcaması olan ama pencere verisi olmayan platformlar —
  /// bu satırlar platformun KENDİ varsayılanıyla sayıldı. Arayüz bunu bir
  /// dipnot olarak basmalı, yoksa sayı olduğundan emin görünür.
  unsupportedPlatforms: AdPlatform[];
  /**
   * 🔴 HER ZAMAN `false` — ve bu bir eksiklik değil, kalıcı bir sınır.
   *
   * Atıf modeli temas yolunu bilmeyi gerektirir; hiçbir platform temas yolu
   * vermiyor. Bkz. bu bölümün başlığı.
   */
  modelApplied: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Ortak sorgu girdisi
// ═══════════════════════════════════════════════════════════════════════════

/// Karşılaştırma kipi. `previous` = aynı gün sayısı, bitişik önceki dönem.
export const compareModeSchema = z.enum(['none', 'previous', 'previousYear', 'custom']);
export type CompareMode = z.infer<typeof compareModeSchema>;

/**
 * TÜM analytics uçlarının ortak filtre parçası.
 *
 * Dashboard, otomasyon motoru ve ajan araçları AYNI servisi çağırır — ikinci
 * bir SQL yazılmaz. Motorun okuduğu sayı ile kullanıcının gördüğü sayı
 * farklıysa "kural neden tetiklendi" sorusu cevapsız kalır.
 */
export const metricFilterSchema = z.object({
  from: daySchema,
  to: daySchema,
  compare: compareModeSchema.default('none'),
  compareFrom: daySchema.optional(),
  compareTo: daySchema.optional(),
  platforms: csvParam.pipe(z.array(z.enum(['META', 'GOOGLE', 'TIKTOK', 'LINKEDIN']))).optional(),
  adAccountIds: csvParam.pipe(z.array(z.uuid())).optional(),
  campaignIds: csvParam.pipe(z.array(z.uuid())).optional(),
  adSetIds: csvParam.pipe(z.array(z.uuid())).optional(),
  adIds: csvParam.pipe(z.array(z.uuid())).optional(),
  /// Kampanya adı / hesap adı araması.
  q: searchParam,
  status: csvParam.pipe(z.array(entityStatusSchema)).optional(),
  /// Harcaması bu eşiğin altındaki varlıklar elenir. 5 kuruş harcayan bir
  /// kampanyanın ROAS şampiyonu görünmesini engeller.
  minSpend: z.coerce.number().min(0).optional(),
  /// Atıf penceresi ezmesi. ⚠️ Faz 2'de KABUL EDİLİYOR ama uygulanmıyor —
  /// bkz. atıf bölümündeki not. Verilmezse workspace ayarı, o da yoksa
  /// `DEFAULT_ATTRIBUTION`.
  attributionWindow: attributionWindowSchema.optional(),
  attributionModel: attributionModelSchema.optional(),
});
export type MetricFilter = z.infer<typeof metricFilterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Yanıt şekilleri
// ═══════════════════════════════════════════════════════════════════════════

/// Ham toplamlar. Türetilmişler bunlardan hesaplanır, saklanmaz.
export interface MetricTotals {
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  revenue: number;
  /// Türetilmişler — payda sıfırsa `null`.
  ctr: number | null;
  cpc: number | null;
  cpm: number | null;
  cpa: number | null;
  roas: number | null;
  /// 🔴 `null` BURADA İKİ ŞEY DEMEK: ya platform erişim vermedi, ya da
  /// aralık birden çok günü kapsıyor ve erişim toplanamıyor
  /// (`aggregatableOver: 'singleDay'`). Arayüz ikisini de `—` basar; ayrım
  /// kullanıcı için değil, bu kodu okuyan için önemli.
  reach: number | null;
  frequency: number | null;

  /**
   * Huni — Faz 7. `null` = kapsamdaki HİÇBİR satır bu adımı bildirmedi.
   *
   * Diğer toplamlar gibi `0`a çekilmediler: bkz. `FUNNEL_METRIC_KEYS`
   * başlığındaki "`null` = bilinmiyor, `0` = olmadı" kuralı. Hangi
   * platformun bildirdiği `AnalyticsSummary.funnel`da.
   */
  addToCart: number | null;
  addToCartValue: number | null;
  initiateCheckout: number | null;
  initiateCheckoutValue: number | null;
  purchases: number | null;
  purchaseValue: number | null;

  /**
   * Gösterim payı — Adım 10. `null` = kapsamda gösterim payı bildiren HİÇBİR
   * satır yok (Meta-only kapsam, ya da Google hesabında arama kampanyası yok).
   *
   * Değerler 0..1 aralığında ORAN; yüzdeye çeviren arayüz.
   * `impressionShareOverRange` ile türetiliyor, ham toplam DEĞİLLER —
   * `MetricTotals`taki diğer `null`lanabilir alanlardan farkı bu.
   */
  searchImpressionShare: number | null;
  searchTopImpressionShare: number | null;
  searchAbsoluteTopImpressionShare: number | null;
  searchBudgetLostImpressionShare: number | null;
  searchRankLostImpressionShare: number | null;
}

/// Para birimi kırılımı — karışık kapsamda tek toplam basılamaz.
export interface CurrencyTotals extends MetricTotals {
  currency: string;
}

/// Metrik + önceki dönem + değişim. `previous` yoksa null.
export interface MetricComparison {
  current: number | null;
  previous: number | null;
  /// `(current - previous) / |previous|` × 100. `previous` 0 ya da null ise null —
  /// sıfırdan artışı "%∞" diye göstermek bilgi değil gürültü.
  deltaPct: number | null;
}

export interface AnalyticsSummary {
  state: DataState;
  currency: string | null;
  mixedCurrency: boolean;
  byCurrency: CurrencyTotals[];
  totals: MetricTotals;
  /// Her metriğin dönem karşılaştırması — GB3, KD2.
  comparison: Record<MetricKey, MetricComparison>;
  range: { from: string; to: string };
  compareRange: { from: string; to: string } | null;
  /// Dönüşüm ve gelir sayıları hangi atıf kuralıyla sayıldı. `applied: false`
  /// olduğu sürece bu bir NİYET beyanı: sayılar hâlâ platformun kendi
  /// varsayılanıyla geliyor (Faz 6).
  attribution: AttributionInfo;
  /**
   * Huni sayılarını hangi platform gerçekten bildirdi — Faz 7.
   *
   * Atıftaki `unsupportedPlatforms` ile aynı sebeple var: `SUM` NULL'ları
   * atladığı için karışık bir kapsamda huni sayıları sessizce yalnız bir
   * platformu anlatır, harcama ise hepsini. Arayüz `missingPlatforms` doluyken
   * huni kutularının yanına dipnot basmak zorunda.
   */
  funnel: FunnelCoverage;
  /**
   * Gösterim payını hangi platform bildirdi — Adım 10.
   *
   * `funnel` ile aynı sebeple ayrı: `SUM` sonrası platform kayboluyor ve
   * karışık kapsamda pay yalnız Google'ı, harcama hepsini anlatıyor. Arayüz
   * `missingPlatforms` doluyken pay kutularının yanına dipnot basmalı —
   * ama "yakında" DEĞİL: o platformlarda kavramın karşılığı yok.
   */
  impressionShare: ImpressionShareCoverage;
  freshness: DataFreshness;
}

export const timeseriesQuerySchema = metricFilterSchema.extend({
  /// Birden fazla metrik seçilebilir — grafikte iki eksen.
  metrics: csvParam.pipe(z.array(metricKeySchema).min(1).max(4)),
  /// Bugün yalnızca gün bazlı. Saatlik kırılım platformdan gelmiyor.
  granularity: z.enum(['day']).default('day'),
  /// Kırılımlı seri: her platform/hesap için ayrı çizgi.
  groupBy: z.enum(['none', 'platform', 'account', 'campaign']).default('none'),
});
export type TimeseriesQuery = z.infer<typeof timeseriesQuerySchema>;

export interface TimeseriesPoint {
  date: string;
  values: Partial<Record<MetricKey, number | null>>;
}

export interface TimeseriesSeries {
  /// `groupBy: 'none'` ise `'total'`.
  key: string;
  label: string;
  points: TimeseriesPoint[];
}

export interface TimeseriesResponse {
  state: DataState;
  series: TimeseriesSeries[];
  /// Aralıktaki BOŞ GÜNLER sıfırla doldurulur ki grafik kopmasın; ama
  /// türetilmiş metrikte boş gün `null` kalır (0 CTR yanlış bilgi).
  filledDays: number;
  currency: string | null;
  freshness: DataFreshness;
}

export const breakdownQuerySchema = metricFilterSchema.extend({
  groupBy: z.enum(['platform', 'account', 'campaign', 'status']),
  metric: metricKeySchema.default('spend'),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});

export interface BreakdownSlice {
  key: string;
  label: string;
  value: number;
  /// Pay yüzdesi SUNUCUDA hesaplanır; arayüz ikinci kez bölme yazmasın.
  sharePct: number;
  platform: AdPlatform | null;
}

export interface BreakdownResponse {
  state: DataState;
  slices: BreakdownSlice[];
  /// Limitin dışında kalanların toplamı — pasta "diğer" dilimi.
  otherValue: number;
  total: number;
  currency: string | null;
  freshness: DataFreshness;
}

export const rankingQuerySchema = metricFilterSchema.extend({
  metric: metricKeySchema.default('roas'),
  dir: sortDirSchema,
  limit: z.coerce.number().int().min(1).max(20).default(5),
  level: z.enum(['CAMPAIGN', 'ADSET', 'AD']).default('CAMPAIGN'),
});

export interface RankingRow {
  entityId: string;
  name: string;
  platform: AdPlatform;
  accountName: string;
  status: EntityStatus;
  value: number | null;
  spend: number;
  currency: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hiyerarşi tablosu (kampanya → ad set → reklam)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Çocuk seviyeler AYRI uçlardan gelir (`/campaigns/:id/adsets`,
 * `/adsets/:id/ads`) ki katlanır satırlar TEMBEL yüklensin. Tek çağrıda üç
 * seviyeyi birden döndürmek, kullanıcının hiç açmayacağı binlerce reklamı
 * her sayfa yüklemesinde çekmek demek.
 */
export const hierarchyQuerySchema = metricFilterSchema.extend({
  sort: z
    .enum([
      'spend',
      'impressions',
      'clicks',
      'conversions',
      'revenue',
      'ctr',
      'cpc',
      'cpm',
      'cpa',
      'roas',
      /// ⚠️ Çok günlü aralıkta ikisi de `NULL` — sıralama `NULLS LAST` olduğu
      /// için tablo "hepsi boş" görünür. Arayüz bu kolonlara göre sıralamayı
      /// yalnız tek günlük aralıkta teklif etmeli.
      'reach',
      'frequency',
      /// Faz 7 — huni. ⚠️ Bu kolonlar huni verisi OLMAYAN satırlarda `NULL`
      /// ve sıralama `NULLS LAST`: Google hesapları listenin altına düşüyor.
      /// Bu bir hata değil (o satırlarda gerçekten veri yok) ama arayüz
      /// karışık kapsamda dipnot basmalı — `AnalyticsSummary.funnel`
      /// hangi platformun bildirdiğini söylüyor.
      'addToCart',
      'addToCartValue',
      'initiateCheckout',
      'initiateCheckoutValue',
      'purchases',
      'purchaseValue',
      /// Adım 10 — gösterim payı. ⚠️ Yalnız Google CAMPAIGN/ADSET satırlarında
      /// dolu; `NULLS LAST` olduğu için Meta satırları listenin altına düşer.
      /// Arayüz bu sıralamayı yalnız Google kapsamında teklif etmeli, yoksa
      /// tablo yarısı boş görünür — `AnalyticsSummary.impressionShare`
      /// hangi platformun bildirdiğini söylüyor.
      'searchImpressionShare',
      'searchTopImpressionShare',
      'searchAbsoluteTopImpressionShare',
      'searchBudgetLostImpressionShare',
      'searchRankLostImpressionShare',
      'name',
      'status',
      'dailyBudget',
    ])
    .default('spend'),
  dir: sortDirSchema,
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  /// Sanal kaydırma için — verilirse `page` yok sayılır (KM10).
  cursor: z.string().max(200).optional(),
  /// Görünecek metrik kolonları (KM8). Boşsa varsayılan küme.
  columns: csvParam.pipe(z.array(metricKeySchema).max(10)).optional(),
});
export type HierarchyQuery = z.infer<typeof hierarchyQuerySchema>;

export interface HierarchyRow {
  id: string;
  level: MetricLevel;
  name: string;
  status: EntityStatus;
  platform: AdPlatform;
  adAccountId: string;
  accountName: string;
  currency: string;
  dailyBudget: number | null;
  lifetimeBudget: number | null;
  /// Alt seviyesi var mı — katlama okunun görünüp görünmeyeceği.
  hasChildren: boolean;
  metrics: Partial<Record<MetricKey, number | null>>;
}

export interface HierarchyResponse {
  state: DataState;
  rows: HierarchyRow[];
  /// Görünen SAYFANIN değil, FİLTRENİN tamamının toplamı. Tablo altındaki
  /// "toplam" satırı sayfalamayla değişmemeli.
  totals: MetricTotals;
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  nextCursor: string | null;
  currency: string | null;
  mixedCurrency: boolean;
  freshness: DataFreshness;
}

/// KD1–KD4 — kampanya detayı başlığı ve metrikleri.
export interface CampaignDetail {
  id: string;
  name: string;
  platform: AdPlatform;
  status: EntityStatus;
  objective: string | null;
  adAccountId: string;
  accountName: string;
  currency: string;
  dailyBudget: number | null;
  lifetimeBudget: number | null;
  startTime: string | null;
  stopTime: string | null;
  platformRef: string;
  summary: AnalyticsSummary;
}

/// KM7 / KD9 — toplu ya da tekil yazma. Guardrail'ler automation ile ORTAK:
/// bütçe değişimi `Workspace.maxBudgetChangePercent` tavanını aşamaz ve
/// `Idempotency-Key` başlığı beklenir.
export const entityStatusChangeSchema = z.object({
  targets: z
    .array(
      z.object({
        level: z.enum(['CAMPAIGN', 'ADSET', 'AD']),
        id: z.uuid(),
      }),
    )
    .min(1)
    .max(100),
  /// ARCHIVED yalnız Meta ve LinkedIn'de yazılabilir — `ConnectorCapabilities
  /// .canArchive`. Desteklemeyen platformda uç 400 DEĞİL, hedef başına
  /// `ARCHIVE_UNSUPPORTED` döndürüyor: toplu işlemde bir Google kampanyası
  /// yüzünden Meta kampanyaları da arşivlenmemiş kalmasın.
  status: z.enum(['ACTIVE', 'PAUSED', 'ARCHIVED']),
});
export type EntityStatusChangeInput = z.infer<typeof entityStatusChangeSchema>;

export const budgetChangeSchema = z
  .object({
    level: z.enum(['CAMPAIGN', 'ADSET']),
    id: z.uuid(),
    /// İkisinden tam biri dolu olmalı.
    dailyBudget: z.number().min(0).max(1_000_000).nullable().optional(),
    lifetimeBudget: z.number().min(0).max(10_000_000).nullable().optional(),
  })
  .refine(
    (v) => (v.dailyBudget != null) !== (v.lifetimeBudget != null),
    'Günlük ya da ömür bütçesinden tam birini verin',
  );
export type BudgetChangeInput = z.infer<typeof budgetChangeSchema>;

/// Toplu yazmada kısmi başarı normaldir — platform bazı hedefleri reddedebilir.
export interface BulkActionResult {
  succeeded: { id: string; level: MetricLevel }[];
  failed: { id: string; level: MetricLevel; code: string; message: string }[];
}

// ═══════════════════════════════════════════════════════════════════════════
// Karşılaştırma (§14: ayrı A/B modülü yok, bu uç onun yerine geçiyor)
// ═══════════════════════════════════════════════════════════════════════════

export const comparisonSchema = z
  .object({
    mode: z.enum(['entities', 'periods']),
    /// `entities` kipinde iki varlık kimliği.
    left: z.object({ level: z.enum(['CAMPAIGN', 'ADSET', 'AD']), id: z.uuid() }).optional(),
    right: z.object({ level: z.enum(['CAMPAIGN', 'ADSET', 'AD']), id: z.uuid() }).optional(),
    /// `periods` kipinde iki aralık; filtre ikisinde de aynı.
    leftRange: z.object({ from: daySchema, to: daySchema }).optional(),
    rightRange: z.object({ from: daySchema, to: daySchema }).optional(),
    filter: metricFilterSchema.partial().optional(),
    /// Üst sınır `METRIC_KEYS.length` — varsayılan TÜM metrikler ve sabit bir
    /// sayı (eskiden 10) yazmak, sözlük büyüdüğü an varsayılanı kendi
    /// şemasına aykırı hâle getiriyordu.
    metrics: z
      .array(metricKeySchema)
      .min(1)
      .max(METRIC_KEYS.length)
      .default([...METRIC_KEYS]),
  })
  .refine(
    (v) =>
      v.mode === 'entities' ? Boolean(v.left && v.right) : Boolean(v.leftRange && v.rightRange),
    'Karşılaştırma kipine uygun iki taraf verin',
  );
export type ComparisonInput = z.infer<typeof comparisonSchema>;

export interface ComparisonSide {
  label: string;
  currency: string | null;
  totals: MetricTotals;
}

export interface ComparisonResponse {
  left: ComparisonSide;
  right: ComparisonSide;
  /// Metrik başına fark yüzdesi (sağ, sola göre).
  delta: Record<MetricKey, number | null>;
  /// İki taraf farklı para birimindeyse arayüz para metriklerini
  /// karşılaştırmaz — sadece oranları basar.
  comparable: boolean;
  freshness: DataFreshness;
}

// ═══════════════════════════════════════════════════════════════════════════
// Dışa aktarma
// ═══════════════════════════════════════════════════════════════════════════

/**
 * CSV: geçerli filtre/sıralama/kolon seçimini AYNEN uygular ve UTF-8 BOM ile
 * başlar — BOM olmadan Excel Türkçe karakterleri bozuyor ve kullanıcı bunu
 * "sizin raporunuz bozuk" diye okuyor.
 *
 * Eşiğin (`CSV_SYNC_ROW_LIMIT`) üstünde senkron üretim API sürecini bloklar;
 * o durumda iş kuyruğa alınır ve bildirimle teslim edilir.
 *
 * PDF: HER ZAMAN kuyrukta. Sunucuda headless Chrome var; HTML → PDF. Senkron
 * PDF üretimi tek bir istekle API'yi saniyelerce meşgul eder.
 */
export const CSV_SYNC_ROW_LIMIT = 50_000;

export const exportSchema = z.object({
  format: z.enum(['csv', 'pdf']),
  /// CSV'de tablo, PDF'te rapor görünümü.
  scope: z.enum(['table', 'summary', 'timeseries']).default('table'),
  filter: metricFilterSchema.partial().optional(),
  columns: z.array(metricKeySchema).max(10).optional(),
  /// PDF başlığı ve müşteriye sunulacak not (RG4 yazdırma düzeni).
  title: z.string().trim().max(120).optional(),
  /// Raporun dili — isteyen arayüzün dili (29 Eyl 2026, 13 dil). Yoksa
  /// Türkçe: alandan önceki işler Türkçe basılıyordu, davranış korunuyor.
  locale: localeSchema.optional(),
});
export type ExportInput = z.infer<typeof exportSchema>;

export interface ExportJobResponse {
  jobId: string;
  format: 'csv' | 'pdf';
  /// Senkron üretildiyse dosya doğrudan döner ve bu alan dolu olur.
  downloadUrl: string | null;
  /// İmzalı bağlantının geçerlilik süresi.
  expiresAt: string | null;
  status: 'ready' | 'queued';
}

export interface ExportJobStatus {
  jobId: string;
  status: 'queued' | 'running' | 'ready' | 'failed';
  progress: number;
  downloadUrl: string | null;
  expiresAt: string | null;
  error: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Karbon ayak izi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 BU BİR TAHMİN, ÖLÇÜM DEĞİL — ve sözleşme bunu SAKLAMAZ.
 *
 * Reklam yayınının karbon ayak izi elimizdeki tek girdiden türetiliyor:
 * `metrics_daily.impressions`. Zincir üç halka:
 *
 *     gösterim × (gösterim başına kWh) × (şebeke yoğunluğu gCO2e/kWh)
 *
 * Üç halkanın hiçbiri ölçülmüş değil; üçü de literatürden alınmış ORTALAMA.
 * Gerçek değer, aynı kampanya için 3 kat aşağıda ya da yukarıda olabilir
 * (`uncertaintyFactor`). Bunun somut sebepleri:
 *
 *   • Şebeke yoğunluğu ülkeye göre 30 ile 700 gCO2e/kWh arasında değişiyor ve
 *     gösterimin NEREDE görüldüğünü bilmiyoruz — `metrics_daily`de coğrafya
 *     kolonu yok (şema donmuş).
 *   • Gösterim başına enerji, kreatifin ağırlığına ve izlenme süresine bağlı;
 *     platform bunu satır bazında vermiyor.
 *   • Cihaz payı (telefon mu, akıllı TV mi) tamamen bilinmiyor.
 *
 * ---------------------------------------------------------------------------
 * Sözleşmenin bundan çıkardığı üç kural
 * ---------------------------------------------------------------------------
 * 1. Yanıt HER ZAMAN `methodology` taşır: katsayılar, kaynakları ve belirsizlik
 *    payı. Arayüz "1,4 kg CO2e" yazamaz; "≈1,4 kg CO2e (0,5–4,2)" yazar ve
 *    kaynağı dipnotta gösterir. Kaynak taşımayan bir çevre sayısı, yeşil
 *    aklama (greenwashing) ile aynı şeydir.
 * 2. `totalGrams` GÖSTERİM YOKSA `null` — `0` DEĞİL. `metric-math.ts`teki
 *    kararın aynısı: "veri yok" ile "sıfır emisyon" farklı cümleler ve
 *    ikincisi yalan. Reklam yayınlamayan bir dönem için "0 kg saldınız"
 *    demek, ölçtüğümüzü iddia etmek olurdu.
 * 3. Platform başına AYRI katsayı. Meta'nın otomatik oynayan video akışıyla
 *    Google arama ağının metin reklamı aynı enerjiyi harcamıyor — tek
 *    katsayı kullanmak, kullanıcının "hangi kanal daha kirli" sorusuna
 *    sistematik olarak yanlış cevap verirdi.
 *
 * ---------------------------------------------------------------------------
 * ⚠️ Ne DEĞİL
 * ---------------------------------------------------------------------------
 * Bu bir GHG Protocol Kapsam 3 beyanı değil, denetlenebilir bir envanter
 * değil ve karbon dengeleme (offset) satın alma temeli olarak kullanılamaz.
 * Ürünün vaadi "büyüklük mertebesini gör ve kanallar arasında karşılaştır".
 */

/// Katsayının nereden geldiği. Arayüz dipnotu bu iki alandan kuruyor; serbest
/// metin tek parça olsaydı çeviri dosyası kaynağı da çevirmek zorunda kalırdı.
export interface CarbonFactor {
  /// Sayının kendisi.
  value: number;
  /// Birimi — "gCO2e/kWh", "kWh/1000 gösterim", "gCO2e/km".
  unit: string;
  /// Kaynak kurumu ve yılı. Boş bırakılamaz: kaynaksız katsayı yayınlamıyoruz.
  source: string;
  /// Sayının neyi temsil ettiği ve nerede zayıf olduğu — tek cümle.
  note: string;
}

export interface CarbonMethodology {
  /// Elektrik şebekesinin küresel ortalama karbon yoğunluğu.
  gridIntensity: CarbonFactor;
  /**
   * Platform başına gösterim enerjisi (kWh / 1.000 gösterim).
   *
   * `OPENAI` de var çünkü `AdPlatform` onu taşıyor; bugün adapteri yok, yani
   * satır da doğmuyor. Anahtarı boş bırakmak, yarın o satırlar doğduğunda
   * sessizce `undefined × gösterim = NaN` üretirdi.
   */
  energyPerThousandImpressions: Record<AdPlatform, CarbonFactor>;
  /**
   * Belirsizlik çarpanı. Tahmin bu sayıya BÖLÜNÜP bu sayıyla ÇARPILDIĞINDA
   * gerçek değerin makul olarak düştüğü aralık çıkar (`3` → ÷3 … ×3).
   * Simetrik bir "± yüzde" değil bilerek: hata çarpımsal, toplamsal değil.
   */
  uncertaintyFactor: number;
  /// Anlaşılır eşdeğerler. Kaynakları ayrı çünkü ikisi de farklı kurumdan.
  equivalents: {
    car: CarbonFactor;
    tree: CarbonFactor;
  };
}

/// Platform kırılımı. Katsayı satırla birlikte dönüyor — "Meta neden daha
/// yüksek" sorusunun cevabı tablonun kendisinde olsun, dipnotta değil.
export interface CarbonPlatformSlice {
  platform: AdPlatform;
  impressions: number;
  /// Gösterim yoksa `null`.
  grams: number | null;
  /// Toplam içindeki pay. Toplam yoksa `null` — `0` yazmak "hiç katkısı yok"
  /// demek olurdu, oysa doğru cevap "bilinmiyor".
  sharePct: number | null;
  /// Bu satırın hangi katsayıyla çarpıldığı (kWh / 1.000 gösterim).
  energyPerThousandImpressions: number;
}

export interface CarbonDailyPoint {
  date: string;
  impressions: number;
  /// O gün gösterim yoksa `null`. Grafik boşluğu ÇİZMEZ — sıfır çizgisi
  /// "o gün temiz reklam yayınladık" gibi okunur.
  grams: number | null;
}

export interface CarbonCampaignRow {
  entityId: string;
  name: string;
  platform: AdPlatform;
  accountName: string;
  impressions: number;
  grams: number | null;
  sharePct: number | null;
}

/**
 * Anlaşılır eşdeğerler.
 *
 * ⚠️ Eşdeğerler tahminin ÜSTÜNE ikinci bir belirsizlik ekliyor (özellikle
 * ağaç: tür, yaş ve iklime göre 10–30 kg/yıl arasında değişiyor). Arayüz
 * bunları ana sayı gibi değil, "yaklaşık şu kadar" cümlesi olarak basmalı.
 */
export interface CarbonEquivalents {
  /// Ortalama bir otomobilin aynı emisyonu üretmek için gideceği kilometre.
  carKm: number | null;
  /// Aynı emisyonu bir yılda emmek için gereken ağaç sayısı.
  treeYears: number | null;
}

/**
 * `from`/`to` YA DA `preset`.
 *
 * `preset` workspace zaman diliminde çözülüyor (`date-range.util.todayIn`):
 * İstanbul'da gece 01:00'de "bugün" diyen kullanıcı UTC'ye göre daha dün ve
 * grafiğin boş görünmesi bir hata olarak okunur.
 *
 * 🔴 `adIds` / `adSetIds` BİLEREK YOK. Kabul edilselerdi `resolveLevel` seviyeyi
 * derinleştirir, "en çok emisyon üreten 5 kampanya" listesi sessizce
 * "5 reklam" olurdu — aynı alan adı altında başka bir şey. Karbon ekranının
 * kanonik seviyesi CAMPAIGN ve bu sabit.
 */
export const carbonQuerySchema = z
  .object({
    from: daySchema.optional(),
    to: daySchema.optional(),
    /// `custom` verilirse `from`/`to` yine zorunlu.
    preset: relativeRangeSchema.optional(),
    platforms: csvParam.pipe(z.array(z.enum(['META', 'GOOGLE', 'TIKTOK', 'LINKEDIN']))).optional(),
    adAccountIds: csvParam.pipe(z.array(z.uuid())).optional(),
    campaignIds: csvParam.pipe(z.array(z.uuid())).optional(),
    /// Kaç kampanya listelensin. Varsayılan 5 — ekrandaki liste bu kadar.
    topCampaigns: z.coerce.number().int().min(1).max(20).default(5),
  })
  .refine(
    (v) => (v.preset && v.preset !== 'custom' ? true : Boolean(v.from) && Boolean(v.to)),
    'from ve to verin ya da custom olmayan bir preset seçin',
  );
export type CarbonQuery = z.infer<typeof carbonQuerySchema>;

export interface CarbonResponse {
  state: DataState;
  /// Çözülmüş aralık. `preset` gönderildiğinde arayüz hangi günlerin
  /// toplandığını göstermek zorunda — "son 30 gün" etiketi tek başına
  /// hangi 30 gün olduğunu söylemiyor.
  range: { from: string; to: string };
  impressions: number;
  /// 🔴 Gösterim yoksa `null`. Bkz. bölüm başındaki 2. kural.
  totalGrams: number | null;
  /// Belirsizlik aralığının uçları (`totalGrams / factor`, `× factor`).
  /// Arayüz ana sayının yanında bunu göstermeden yayına çıkmamalı.
  totalGramsLow: number | null;
  totalGramsHigh: number | null;
  /// Karışımın yoğunluğu: 1.000 gösterim başına gCO2e. Platform dağılımı
  /// videoya kaydıkça büyür — kullanıcının tek bakışta göreceği sinyal bu.
  gramsPerThousandImpressions: number | null;
  byPlatform: CarbonPlatformSlice[];
  /// Aralıktaki HER gün — veri olmayan günler `grams: null` ile.
  daily: CarbonDailyPoint[];
  /// En çok emisyon üreten kampanyalar. Gösterimi olmayan kampanya listeye
  /// GİRMEZ: "0 g" satırı bir "en çok" listesinde yalnızca gürültü.
  topCampaigns: CarbonCampaignRow[];
  equivalents: CarbonEquivalents;
  methodology: CarbonMethodology;
  freshness: DataFreshness;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hata kodları
// ═══════════════════════════════════════════════════════════════════════════

export const AnalyticsErrorCode = {
  /// Aralık `MAX_RANGE_DAYS`'i aşıyor.
  RANGE_TOO_LARGE: 'RANGE_TOO_LARGE',
  RANGE_INVALID: 'RANGE_INVALID',
  METRIC_NOT_AVAILABLE_AT_LEVEL: 'METRIC_NOT_AVAILABLE_AT_LEVEL',
  /// Dışa aktarma işi yok ya da başka kiracıya ait. Ad tarihsel (kaydedilmiş
  /// raporlar 24 Eyl 2026'da kaldırıldı); API sözleşmesi olduğu için korunuyor.
  REPORT_NOT_FOUND: 'REPORT_NOT_FOUND',
  EXPORT_TOO_LARGE: 'EXPORT_TOO_LARGE',
  /// PDF üretim yığını yapılandırılmamış (headless Chrome yolu yok).
  PDF_NOT_CONFIGURED: 'PDF_NOT_CONFIGURED',
  /// Kapsamda birden çok para birimi var ve istenen işlem tek toplam gerektiriyor.
  MIXED_CURRENCY: 'MIXED_CURRENCY',
  CAMPAIGN_NOT_FOUND: 'CAMPAIGN_NOT_FOUND',
} as const;
export type AnalyticsErrorCode = (typeof AnalyticsErrorCode)[keyof typeof AnalyticsErrorCode];
